/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable sonarjs/cognitive-complexity */
import { cn, Stack } from 'component-library';
import { FC, forwardRef, Fragment, HTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';
import {
  Area,
  Bar,
  Cell,
  ComposedChart,
  LabelList,
  Line,
  Pie,
  PieChart,
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ReferenceLine,
  TooltipProps,
} from 'recharts-overriden';

import { CustomBenchmarkSuccessResponse } from '@/api/rest/resources/types/benchmark';
import { BenchmarkType, PlotReportFactLabel } from '@/api/rest/resources/types/fact';
import { UnitEnum } from '@/api/rest/resources/types/units';
import { ChartLegendBar } from '@/components/Charts';
import { Chart } from '@/components/Charts/components/Chart';
import { RadialGradient } from '@/components/Charts/components/RadialGradient';
import { RenderBenchmarkReferenceLine } from '@/components/Charts/components/ReferenceLine';
import {
  ChartTooltipDot,
  ChartTooltipTextPrimary,
  ChartTooltipTextSecondary,
} from '@/components/Charts/components/Tooltip';
import { ChartLegendBarItem } from '@/components/Charts/components/types';
import { SHARED_CHART_COLORS } from '@/components/Charts/constants';
import { RenderReferenceLine } from '@/components/Charts/ReferenceLines';
import { GraphFact } from '@/components/Charts/types';
import { getBenchmarkLabel, getChartDataFromGraphFact, sortChartData } from '@/components/Charts/utils';
import { getDisplayNumber } from '@/hooks/useDisplayNumber';
import { isNullish } from '@/utils/booleans';
import { SortOrder } from '@/utils/constants';
import { valueToTonne } from '@/utils/formatting';

export enum ChartShape {
  radar = 'radar',
  area = 'area',
  bar = 'bar',
  line = 'line',
  pie = 'pie',
}

export type PieChartProps = {
  innerRadius?: number;
  outerRadius?: number;
  startAngle?: number;
  endAngle?: number;
  paddingAngle?: number;
  cx?: string;
  cy?: string;
};

export type ChartElementConfig = {
  shape: ChartShape;
  dataKey: string;
  label: string;
  color: string;
  barSize?: number;
  key: string;
  // TODO: MVP-CW - update this map key and id better for existing graphs
  id: string;
  unit?: string;
  formatter?: (value: any) => any;
  className?: string;
  showLabel?: boolean;
  labelPosition?: 'top' | 'center' | 'bottom' | 'left' | 'right';
  hideInTooltip?: boolean;
  [key: string]: any;
} & PieChartProps;

export type PieChartConfig = Omit<ChartElementConfig, 'shape' | 'label'> & {
  shape: ChartShape.pie;
  label?: (value: any) => JSX.Element | string;
};

export type ReferenceLineConfig = {
  y: number;
  label?: string;
  dataKey: string;
};

export type ThresholdConfig = {
  value: number;
  label?: string;
};

export type CompositeGraphProps = HTMLAttributes<HTMLDivElement> & {
  data: any;
  chartConfig: ChartElementConfig[];
  xAxisProps?: any;
  yAxisProps?: any;
  legendItems?: ChartLegendBarItem[];
  referenceLines?: ReferenceLineConfig[];
  thresholds?: ThresholdConfig[];
  tooltip?: (props: TooltipProps<ValueType, NameType>) => JSX.Element;
  margin?: { top?: number; right?: number; bottom?: number; left?: number };
  tooltipNumberFormatOptions?: Intl.NumberFormatOptions;
  sortData?: SortOrder;
  customBenchmark?: CustomBenchmarkSuccessResponse;
};

const isGraphFact = (fact: GraphFact): fact is GraphFact => !!fact?.date;

export const CompositeGraph = forwardRef<HTMLDivElement, CompositeGraphProps>(
  (
    {
      data,
      chartConfig,
      xAxisProps = {},
      yAxisProps = {},
      legendItems = [],
      referenceLines = [],
      thresholds = [],
      tooltip,
      margin,
      tooltipNumberFormatOptions,
      sortData,
      customBenchmark,
      ...delegated
    },
    ref,
  ) => {
    const { t } = useTranslation();

    let chartData: any[] = data ?? [];
    let computedXAxisProps = xAxisProps;
    const hasPolarCharts = chartConfig.some((cfg) => cfg.shape === ChartShape.radar);
    const hasPieCharts = chartConfig.some((cfg) => cfg.shape === ChartShape.pie);

    const isGraphFactArray = chartData.length > 0 && isGraphFact(chartData[0]) && !hasPieCharts;
    if (isGraphFactArray) {
      const factNamesDict = chartConfig.reduce(
        (acc, cur) => {
          acc[`${cur.key}`] = cur.id;
          return acc;
        },
        {} as Record<string, string>,
      );
      const formatter = chartConfig.find((cfg) => typeof cfg.formatter === 'function')?.formatter;
      const chartDataMap = getChartDataFromGraphFact(data, factNamesDict, formatter);
      chartData = sortChartData(chartDataMap, (date) => new Date(date).getTime());

      // Set up x-axis configuration to only show years with actual data
      // This prevents gaps and ensures uniform tick spacing for available data points
      if (chartData.length > 0) {
        // For GraphFact data, we want to show ticks only for years that have data
        // The chart will automatically space the ticks evenly based on the data points
        computedXAxisProps = {
          dataKey: 'label',
          type: 'category',
          ...xAxisProps,
        };
      }
    }

    if (sortData) {
      const sortKey = xAxisProps?.dataKey;
      if (sortKey) {
        chartData = [...chartData].sort((a, b) => {
          const comparison = String(a[`${sortKey}`] ?? '').localeCompare(String(b[`${sortKey}`] ?? ''));
          return sortData === SortOrder.ZA ? -comparison : comparison;
        });
      }
    }

    // Inject each reference line's y-value into every chartData point so RenderReferenceLine can read it
    const chartDataWithRefLines =
      referenceLines.length > 0 && chartData.length > 0
        ? referenceLines.reduce((acc, refLine) => {
            const key = refLine.dataKey;
            return acc.map((point) => ({ ...point, [key]: { value: refLine.y } }));
          }, chartData)
        : chartData;

    const resolvedTick = xAxisProps?.tick
      ? (props: { x: number; y: number; payload: { value: string } }) => {
          const { x, y, payload } = props;
          const cfg = xAxisProps.tick;
          return (
            <g transform={`translate(${x},${y})`}>
              <text x={cfg.x} y={cfg.y} textAnchor={cfg.textAnchor} transform={cfg.transform} style={cfg.style}>
                {payload.value}
              </text>
            </g>
          );
        }
      : null;

    if (hasPolarCharts) {
      return (
        <>
          <Chart.Container
            {...delegated}
            data={chartData}
            config={chartConfig.reduce(
              (acc, cur) => {
                acc[cur.dataKey] = { label: cur.label, color: cur.color };
                return acc;
              },
              {} as Record<string, { label: string; color: string }>,
            )}
          >
            <RadarChart data={chartData}>
              {chartConfig
                .filter((cfg) => cfg.shape === ChartShape.radar)
                .map((cfg) => {
                  const commonProps = {
                    activeDot: cfg.activeDot !== undefined ? cfg.activeDot : ChartTooltipDot,
                  };
                  return (
                    <Fragment key={`radar-group-${cfg.dataKey}`}>
                      <defs key={`defs-${cfg.dataKey}`}>
                        {cfg.gradient && (
                          <RadialGradient
                            id={cfg.gradient.id}
                            cx={cfg.gradient.cx}
                            cy={cfg.gradient.cy}
                            r={cfg.gradient.r}
                            gradientUnits={cfg.gradient.gradientUnits}
                            stops={cfg.gradient.stops}
                          />
                        )}
                      </defs>
                      <PolarGrid key={`radar-grid-${cfg.dataKey}`} />
                      {yAxisProps && <PolarRadiusAxis key={`radar-radius-${cfg.dataKey}`} {...yAxisProps} />}
                      <Radar
                        stroke={cfg.color}
                        fill={cfg.fill}
                        {...{ ...cfg, shape: undefined, gradient: undefined }}
                        {...commonProps}
                        key={`radar-${cfg.dataKey}`}
                      />
                      <PolarAngleAxis key={`radar-axis-${cfg.dataKey}`} {...computedXAxisProps} />
                    </Fragment>
                  );
                })}
              <Chart.Tooltip
                content={
                  tooltip || ((props) => <CustomTooltip {...props} xAxisProps={xAxisProps} chartConfig={chartConfig} />)
                }
              />
            </RadarChart>
          </Chart.Container>
          {legendItems.length > 0 && <ChartLegendBar items={legendItems} className='sm:ml-[28px]' />}
        </>
      );
    }

    if (hasPieCharts) {
      return (
        <PieChartGraph
          {...delegated}
          chartData={chartData}
          chartConfig={chartConfig}
          legendItems={legendItems}
          tooltip={tooltip}
          xAxisProps={xAxisProps}
        />
      );
    }

    return (
      <>
        <Chart.Container
          {...delegated}
          ref={ref}
          data={chartData}
          config={chartConfig.reduce(
            (acc, cur) => {
              acc[cur.dataKey] = { label: cur.label, color: cur.color };
              return acc;
            },
            {} as Record<string, { label: string; color: string }>,
          )}
        >
          <ComposedChart data={chartDataWithRefLines} margin={margin}>
            {chartConfig.map((cfg) => {
              const commonProps = {
                activeDot: cfg.activeDot !== undefined ? cfg.activeDot : ChartTooltipDot,
                stackId: cfg.stackId,
              };
              if (cfg.shape === ChartShape.bar) {
                return (
                  <Bar
                    fill={cfg.fill}
                    barSize={cfg.barSize || 20}
                    {...{ ...cfg, shape: undefined }}
                    {...commonProps}
                    key={cfg.dataKey}
                  >
                    {chartDataWithRefLines.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={cfg.barColors[index % cfg.barColors.length]} />
                    ))}
                    {cfg.showLabel && (
                      <LabelList
                        dataKey={cfg.dataKey}
                        position='center'
                        content={({ value, x, y, width, height, payload }: any) => {
                          const individualValue = payload?.[cfg?.dataKey] ?? value;

                          if (!individualValue || individualValue === 0) return null;

                          const displayValue = cfg.formatter ? cfg.formatter(individualValue) : individualValue;
                          const unit = cfg.unit || '';
                          const labelText = `${displayValue}${unit}`;

                          const centerX = (x as number) + (width as number) / 2;
                          const centerY = (y as number) + (height as number) / 2;

                          return (
                            <text
                              x={centerX}
                              y={centerY}
                              textAnchor='middle'
                              dominantBaseline='middle'
                              fill='black'
                              fontSize='12'
                              fontWeight='500'
                            >
                              {labelText}
                            </text>
                          );
                        }}
                      />
                    )}
                  </Bar>
                );
              }
              if (cfg.shape === ChartShape.line) {
                return (
                  <Line
                    connectNulls
                    isAnimationActive={false}
                    stroke={cfg.color}
                    dot={{ stroke: cfg.color }}
                    {...cfg}
                    {...commonProps}
                    key={cfg.dataKey}
                  />
                );
              }
              if (cfg.shape === ChartShape.area) {
                return <Area stroke={cfg.color} fill={cfg.color} {...cfg} {...commonProps} key={cfg.dataKey} />;
              }
              return null;
            })}
            {typeof customBenchmark?.low_value === 'number' &&
              RenderBenchmarkReferenceLine({
                value: customBenchmark.low_value,
                benchmark: BenchmarkType.low,
                labelOverride: getBenchmarkLabel(true, t('global.benchmark.low'), customBenchmark.name),
              })}
            {typeof customBenchmark?.moderate_value === 'number' &&
              RenderBenchmarkReferenceLine({
                value: customBenchmark.moderate_value,
                benchmark: BenchmarkType.moderate,
                labelOverride: getBenchmarkLabel(true, t('global.benchmark.moderate'), customBenchmark.name),
              })}
            {typeof customBenchmark?.high_value === 'number' &&
              RenderBenchmarkReferenceLine({
                value: customBenchmark.high_value,
                benchmark: BenchmarkType.high,
                labelOverride: getBenchmarkLabel(true, t('global.benchmark.high'), customBenchmark.name),
              })}
            <Chart.XAxis
              {...{ ...(Chart.XAxis.defaultProps || {}), ...{ ...computedXAxisProps, label: '' } }}
              {...(resolvedTick ? { tick: resolvedTick, interval: 0 } : {})}
            >
              {computedXAxisProps?.label && <Chart.Label>{computedXAxisProps.label}</Chart.Label>}
            </Chart.XAxis>

            <Chart.YAxis {...{ ...(Chart.YAxis.defaultProps || {}), ...{ ...yAxisProps, label: '' } }}>
              {yAxisProps?.label && <Chart.Label {...yAxisProps?.labelProps}>{yAxisProps.label}</Chart.Label>}
            </Chart.YAxis>
            {referenceLines.map((refLine, index) => {
              const refKey = refLine.dataKey;
              return RenderReferenceLine({
                dataKey: `${refKey}.value`,
                type: 'linear',
                label: refLine.label,
                chartData: chartDataWithRefLines,
                id: `ref-line-${index}`,
              });
            })}
            {thresholds.map((threshold, index) => (
              <ReferenceLine
                key={`threshold-${index}`}
                y={threshold.value}
                stroke={SHARED_CHART_COLORS.referenceLines.stroke}
                strokeDasharray='4 4'
                label={
                  threshold.label
                    ? {
                        value: threshold.label,
                        position: 'insideBottomRight',
                        fill: SHARED_CHART_COLORS.referenceLines.label,
                      }
                    : undefined
                }
              />
            ))}
            <Chart.Tooltip
              allowEscapeViewBox={{ x: false, y: true }}
              content={
                tooltip ||
                ((props) => (
                  <CustomTooltip
                    {...props}
                    xAxisProps={computedXAxisProps}
                    chartConfig={chartConfig}
                    referenceLines={referenceLines}
                    tooltipNumberFormatOptions={tooltipNumberFormatOptions}
                  />
                ))
              }
            />
          </ComposedChart>
        </Chart.Container>
        {legendItems.length > 0 && <ChartLegendBar items={legendItems} className='sm:ml-[28px] my-4' />}
      </>
    );
  },
);

CompositeGraph.displayName = 'CompositeGraph';

const getPieChartData = (chartData: any[], chartConfig: ChartElementConfig[]) => {
  const pieConfigs = chartConfig.filter((cfg) => cfg.shape === ChartShape.pie);

  if (pieConfigs.length === 0) {
    return [];
  }

  const hasColorsOrLabels = pieConfigs.some((c) => c?.color || c?.label);
  const hasLabelMatching = pieConfigs.some((c) => c?.labels);

  if (!hasColorsOrLabels && !hasLabelMatching) {
    return chartData;
  }

  return chartData.map((entry) => {
    const configMatch = pieConfigs.find((cfg) =>
      cfg.labels?.every((cfgLabel: PlotReportFactLabel) =>
        entry.labels?.some(
          (factLabel: PlotReportFactLabel) => factLabel.name === cfgLabel.name && factLabel.value === cfgLabel.value,
        ),
      ),
    );

    return {
      ...entry,
      color: configMatch?.color ?? entry.color,
      label: configMatch?.label ?? entry.label,
    };
  });
};

type PieChartsGraphProps = HTMLAttributes<HTMLDivElement> & {
  chartData: any[];
  chartConfig: ChartElementConfig[];
  legendItems: ChartLegendBarItem[];
  tooltip?: (props: TooltipProps<ValueType, NameType>) => JSX.Element;
  xAxisProps?: any;
};

const PieChartGraph: FC<PieChartsGraphProps> = ({
  chartData,
  chartConfig,
  legendItems,
  tooltip,
  xAxisProps,
  ...delegated
}) => {
  const pieConfigRaw = chartConfig.find((cfg) => cfg.shape === ChartShape.pie);
  if (!pieConfigRaw) return null;
  const pieConfig = pieConfigRaw as unknown as PieChartConfig;

  const resolvedChartData = getPieChartData(chartData, chartConfig);

  const chartProps = {
    ...pieConfig,
    cx: pieConfig?.cx || '50%',
    cy: pieConfig?.cy || '50%',
    innerRadius: pieConfig?.innerRadius || 0,
    outerRadius: pieConfig?.outerRadius || 80,
    paddingAngle: pieConfig?.paddingAngle || 0,
    dataKey: pieConfig?.dataKey || 'value',
    startAngle: pieConfig?.startAngle || 0,
    endAngle: pieConfig?.endAngle || 360,
    label: ({ value, payload }: any) => {
      const unit = payload?.unit ?? '';
      const valueDisplay = getDisplayNumber(value, window.navigator.language);
      return `${valueDisplay}${unit}`;
    },
    labelLine: pieConfig?.labelLine ?? false,
  };

  return (
    <Stack className='w-full'>
      <Chart.Container
        {...delegated}
        data={resolvedChartData}
        config={chartConfig.reduce(
          (acc, cur) => {
            acc[cur.dataKey] = { label: cur.label, color: cur.color };
            return acc;
          },
          {} as Record<string, { label: any; color: string }>,
        )}
        height={pieConfig?.height || 300}
      >
        <PieChart>
          <Pie data={resolvedChartData} {...chartProps}>
            {resolvedChartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} className='outline-none' />
            ))}
          </Pie>
          <Chart.Tooltip
            content={
              tooltip || ((props) => <CustomTooltip {...props} xAxisProps={xAxisProps} chartConfig={chartConfig} />)
            }
          />
        </PieChart>
      </Chart.Container>
      <ChartLegendBar items={legendItems} className='my-4 w-full items-center justify-center' />
    </Stack>
  );
};

const CustomTooltip = ({
  active,
  payload,
  label,
  xAxisProps,
  chartConfig,
  referenceLines,
  tooltipNumberFormatOptions,
}: TooltipProps<ValueType, NameType> & {
  xAxisProps?: any;
  chartConfig?: ChartElementConfig[];
  referenceLines?: ReferenceLineConfig[];
  tooltipNumberFormatOptions?: Intl.NumberFormatOptions;
}) => {
  const { t } = useTranslation();
  if (!active || !payload?.length) return null;

  const tickFormatter = xAxisProps?.tickFormatter;
  const formattedLabel = tickFormatter ? tickFormatter(label) : label;

  const configMap = Array.isArray(chartConfig)
    ? chartConfig.reduce(
        (acc, cur) => {
          acc[cur.dataKey] = {
            label: cur.label || cur.dataKey,
            unit: cur.unit || '',
            className: cur.className || '',
            hideInTooltip: cur.hideInTooltip || false,
          };
          return acc;
        },
        {} as Record<string, { label: string; unit: string; className: string; hideInTooltip?: boolean }>,
      )
    : {};
  const isPieChart = chartConfig?.some((config) => config.shape === ChartShape.pie);

  let payloadWithoutReferenceLines = payload;
  if (Array.isArray(referenceLines)) {
    const referenceLineDataKeys = referenceLines.map((line) => `${line.dataKey}.value`);

    payloadWithoutReferenceLines = payload.filter((item) => !referenceLineDataKeys.includes(item.dataKey as string));
  }

  return (
    <Stack direction='col' spacing={1.5} className='rounded-md bg-white-100 px-2 py-1 elevation-1'>
      <Stack spacing={2}>
        <ChartTooltipTextSecondary>{formattedLabel}</ChartTooltipTextSecondary>
        {payloadWithoutReferenceLines
          .sort((a, b) => ((a.value ?? 0) > (b.value ?? 0) ? -1 : 1))
          .map((item) => {
            const config = configMap[item.dataKey as string];
            if (config?.hideInTooltip) {
              return null;
            }
            let itemValue = item.value;
            const itemLabel = isPieChart ? item.payload?.label : config?.label;
            const displayValue = !isNullish(itemValue)
              ? `${getDisplayNumber(itemValue as string, window.navigator.language, tooltipNumberFormatOptions)} ${config?.unit}`
              : t('global.analysis.noData');

            if ((item.dataKey as string).includes('health')) {
              const itemId = (item.dataKey as string)?.replace('.value', '') as string;
              itemValue = item.value;
              const itemValueDisplay = `${getDisplayNumber(itemValue as string, window.navigator.language)} ${'%'}`;

              let itemRawValue = item.payload[`${itemId}`]?.metadata?.raw_value ?? '';
              itemRawValue =
                itemId.includes('carbon') || itemId.includes('soil')
                  ? valueToTonne(itemRawValue, UnitEnum['kg/ha'])
                  : itemRawValue;
              const itemRawValueDisplay = `${getDisplayNumber(itemRawValue as string, window.navigator.language)} ${config?.unit}`;

              return (
                <Stack
                  spacing={8}
                  direction='row'
                  key={item.dataKey}
                  className={cn('justify-between', config?.className)}
                >
                  <ChartTooltipTextPrimary>{config?.label}</ChartTooltipTextPrimary>
                  <Stack direction='row' spacing={2}>
                    <ChartTooltipTextSecondary>{itemRawValueDisplay}</ChartTooltipTextSecondary>
                    <ChartTooltipTextPrimary>{itemValueDisplay}</ChartTooltipTextPrimary>
                  </Stack>
                </Stack>
              );
            }

            return (
              <Stack
                spacing={8}
                direction='row'
                key={item.dataKey}
                className={cn('justify-between', config?.className)}
              >
                <ChartTooltipTextPrimary>{itemLabel}</ChartTooltipTextPrimary>
                <ChartTooltipTextPrimary>{displayValue}</ChartTooltipTextPrimary>
              </Stack>
            );
          })}
      </Stack>
    </Stack>
  );
};
